/**
 * SchoolClass Edit Form Component
 * Reference implementation following UI Standards
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Stack, FormControlLabel, Checkbox,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { SchoolClassUpdate } from '../../types/schoolClass';
import { Subject } from '../../types/subject';
import { GRADE_LEVELS } from '../../types/common';

const SchoolClassEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Dropdown data
  const [teachers, setTeachers] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);

  const [formData, setFormData] = useState<SchoolClassUpdate>({
    name: '',
    subject: '',
    grade_level: 0,
    school_year: '',
    description: '',
    teacher_id: 0,
    is_active: true,
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  // Load teachers + subjects for dropdowns
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [teachersRes, subjectsRes] = await Promise.all([
          axios.get('/api/teachers/'),
          axios.get('/api/subjects/?ordering=name&page_size=1000'),
        ]);
        setTeachers(teachersRes.data.results || []);
        setSubjects(subjectsRes.data.results || []);
      } catch (err: any) {
        console.error('Error loading dropdown data:', err);
      } finally {
        setLoadingDropdowns(false);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const fetchSchoolClass = async () => {
      if (!id) {
        setError('No class ID provided');
        setFetchingData(false);
        return;
      }

      try {
        const response = await axios.get(`/api/school-classes/${id}`);
        const schoolClass = response.data;

        setFormData({
          name: schoolClass.name || '',
          subject: schoolClass.subject || '',
          grade_level: schoolClass.grade_level || 0,
          school_year: schoolClass.school_year || '',
          description: schoolClass.description || '',
          teacher_id: schoolClass.teacher_id || 0,
          is_active: schoolClass.is_active ?? true,
        });
      } catch (err: any) {
        console.error('Error fetching class:', err);
        if (err.response?.status === 404) {
          setError('Class not found');
        } else {
          setError('Failed to load class data. Please try again.');
        }
      } finally {
        setFetchingData(false);
      }
    };

    fetchSchoolClass();
  }, [id]);

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? 0 : Number(value)) : value)
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validate()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const payload: SchoolClassUpdate = {};

      if (formData.name && formData.name.trim() !== '') payload.name = formData.name;
      if (formData.subject && formData.subject.trim() !== '') payload.subject = formData.subject;
      if (formData.grade_level !== undefined) payload.grade_level = formData.grade_level;
      if (formData.school_year && formData.school_year.trim() !== '') payload.school_year = formData.school_year;
      if (formData.description && formData.description.trim() !== '') payload.description = formData.description;
      if (formData.teacher_id !== undefined) payload.teacher_id = formData.teacher_id;
      if (formData.is_active !== undefined) payload.is_active = formData.is_active;

      await axios.patch(`/api/school-classes/${id}/`, payload);
      setSuccess(true);

      setTimeout(() => {
        navigate('/classes');
      }, 1500);
    } catch (err: any) {
      console.error('Error updating class:', err);

      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          setError(err.response.data.detail);
        } else if (Array.isArray(err.response.data.detail)) {
          const errorMessages = err.response.data.detail
            .map((e: any) => `${e.loc.join('.')}: ${e.msg}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError('Failed to update class. Please check your input.');
        }
      } else {
        setError('Failed to update class. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !formData.name) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/classes')} sx={{ mt: 2 }}>
          Back to Classes
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink component="button" variant="body1" onClick={() => navigate('/classes')} sx={{ cursor: 'pointer', textDecoration: 'none' }}>
          Classes
        </MuiLink>
        <Typography color="text.primary">Edit Class</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography variant="h4" gutterBottom>Edit Class</Typography>
        <Typography variant="body2" color="textSecondary">Update the class details</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Class updated successfully! Redirecting...</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth label="Class Name" name="name"
                value={formData.name || ''} onChange={handleChange}
                error={!!validationErrors.name} helperText={validationErrors.name}
                disabled={loading}
              />
              <FormControl fullWidth required error={!!validationErrors.subject}>
                <InputLabel>Subject</InputLabel>
                <Select
                  name="subject"
                  value={formData.subject || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  label="Subject"
                  disabled={loading || loadingDropdowns}
                >
                  {subjects.map((subject) => (
                    <MenuItem key={subject.id} value={subject.name}>
                      {subject.name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.subject && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {validationErrors.subject}
                  </Typography>
                )}
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth error={!!validationErrors.grade_level}>
                <InputLabel>Grade Level</InputLabel>
                <Select
                  name="grade_level"
                  value={formData.grade_level || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, grade_level: Number(e.target.value) }));
                    if (validationErrors.grade_level) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.grade_level;
                        return newErrors;
                      });
                    }
                  }}
                  label="Grade Level"
                  disabled={loading || loadingDropdowns}
                >
                  {GRADE_LEVELS.map((grade) => (
                    <MenuItem key={grade.value} value={grade.value}>
                      {grade.label}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.grade_level && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {validationErrors.grade_level}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth error={!!validationErrors.teacher_id}>
                <InputLabel>Teacher</InputLabel>
                <Select
                  name="teacher_id"
                  value={formData.teacher_id || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, teacher_id: Number(e.target.value) }));
                    if (validationErrors.teacher_id) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.teacher_id;
                        return newErrors;
                      });
                    }
                  }}
                  label="Teacher"
                  disabled={loading || loadingDropdowns}
                >
                  {teachers.map((teacher) => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacher.first_name} {teacher.last_name}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.teacher_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {validationErrors.teacher_id}
                  </Typography>
                )}
              </FormControl>
            </Stack>

            <TextField
              fullWidth label="Description" name="description"
              value={formData.description || ''} onChange={handleChange}
              disabled={loading} multiline rows={3}
            />

            <TextField
              fullWidth
              label="School Year"
              name="school_year"
              value={formData.school_year || ''}
              onChange={handleChange}
              disabled={loading}
              helperText="e.g., 2024, 2024/2025"
              placeholder="2024"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active ?? true}
                  onChange={handleChange}
                  name="is_active"
                  disabled={loading}
                />
              }
              label="Active"
            />

            <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
              <Button variant="outlined" onClick={() => navigate('/classes')} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit" variant="contained" color="primary" disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Updating...' : 'Update Class'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default SchoolClassEditForm;
